import { computed, inject, ref, watch } from 'vue'
import { RouteLocationNormalizedLoaded, useRoute } from 'vue-router'
import {
  getAllCustomFieldLocation,
  getAllCustomValLocation,
  getBusinessCustomFieldLocation,
  removeOpportunityCustomFields,
} from '../utils'

import { TextInputIcon } from '@gohighlevel/ghl-icons/24/outline'
import { renderIcon } from '@gohighlevel/ghl-ui'
import { cloneDeep } from 'lodash'
import { useVueI18n } from '../locales'
import { CustomValuesService } from '../services'
import type { CustomObjectLabels } from '../types'

const loading = ref(false)
const locationStaticCustomValues = ref([])
const locationCustomValues = ref([])
const locationCustomFields = ref([])
const staticCustomValues: any = ref([])
const staticCustomValuesWithoutOpportunity: any = ref([])

export const useCustomValues = (
  menuId = 'customValues' as string,
  getCustomObjectLabels: () => CustomObjectLabels = () => ({})
) => {
  const t = useVueI18n()
  let route = useRoute()
  if (!route) {
    route = inject<RouteLocationNormalizedLoaded>(
      'route'
    ) as RouteLocationNormalizedLoaded
  }
  const getCustomValuesAndFieldsForLocation = async () => {
    try {
      loading.value = true
      const { data } = await CustomValuesService.getCustomValueData({
        locationId: (route.params.locationId ||
          route.params.location_id) as string,
      })

      // removing the user email signature from the custom values
      if (data?.static) {
        data.static = data.static.map(item => {
          if (item.value === '__user__' && item.children) {
            item.children = item.children.filter(
              child => child.value !== '{{user.email_signature}}'
            )
          }
          return item
        })
      }
      locationStaticCustomValues.value = data['static'] || []
      locationCustomValues.value = data['custom-values'] || []
      locationCustomFields.value = data['custom-fields'] || []
      setCustomValues()
    } catch (error) {
      console.error(error)
    } finally {
      loading.value = false
    }
  }

  const customMenuOptions = computed(() => [
    {
      menuId,
      menuTitle: t('common.customValues'),
      icon: renderIcon(TextInputIcon),
      renderIcon: true,
      renderTitle: false,
      items: staticCustomValues.value,
    },
  ])

  const customMenuOptionsWithoutOpp = computed(() => [
    {
      menuId,
      menuTitle: t('common.customValues'),
      icon: renderIcon(TextInputIcon),
      renderIcon: true,
      renderTitle: false,
      items: staticCustomValuesWithoutOpportunity.value,
    },
  ])

  const setCustomValues = () => {
    let customValueOptions: any = []
    let businessNode: any = null
    let customFieldOptions: any = []
    let customFieldOptionsWithoutOpp: any = []

    if (locationCustomValues.value && locationCustomValues.value.length > 0) {
      customValueOptions = getAllCustomValLocation(locationCustomValues.value)
    }

    if (locationCustomFields.value && locationCustomFields.value.length > 0) {
      customFieldOptions = getAllCustomFieldLocation(locationCustomFields.value)
      businessNode = getBusinessCustomFieldLocation(locationCustomFields.value)

      const cfOptionsCopy = cloneDeep(customFieldOptions)
      customFieldOptionsWithoutOpp =
        removeOpportunityCustomFields(cfOptionsCopy)
    }

    staticCustomValues.value = [
      ...getStaticCustomValueOptions(customFieldOptions, businessNode),
      ...customValueOptions,
    ]

    staticCustomValuesWithoutOpportunity.value = [
      ...getStaticCustomValueOptions(customFieldOptionsWithoutOpp, businessNode),
      ...customValueOptions,
    ]
  }

  const getStaticCustomValueOptions = (
    options: any = [],
    businessNode: any = null
  ) => {
    let staticCustomValueOptions: any = []
    if (
      locationStaticCustomValues.value &&
      locationStaticCustomValues.value.length > 0
    ) {
      staticCustomValueOptions = cloneDeep(locationStaticCustomValues.value)
    }
    const labels = getCustomObjectLabels() || {}
    const contactElemPos = staticCustomValueOptions?.findIndex(
      (obj: any) => obj.value === '__contact__'
    )
    if (contactElemPos >= 0) {
      const contactLabel = labels.contact?.singular
      if (contactLabel) {
        staticCustomValueOptions[contactElemPos].label = contactLabel
      }
      staticCustomValueOptions[contactElemPos].children = [
        ...staticCustomValueOptions[contactElemPos].children,
        options,
      ]
    }

    if (businessNode) {
      const businessLabel = labels.business?.singular
      const finalBusinessNode = businessLabel
        ? { ...businessNode, label: businessLabel }
        : businessNode
      const insertIdx =
        contactElemPos >= 0 ? contactElemPos + 1 : staticCustomValueOptions.length
      staticCustomValueOptions.splice(insertIdx, 0, finalBusinessNode)
    }

    return staticCustomValueOptions
  }

  watch(
    () => [
      locationStaticCustomValues.value,
      locationCustomValues.value,
      locationCustomFields.value,
      getCustomObjectLabels(),
    ],
    () => {
      setCustomValues()
    },
    { immediate: true }
  )

  return {
    loading,
    customMenuOptions,
    customMenuOptionsWithoutOpp,
    staticCustomValuesWithoutOpportunity,
    staticCustomValues,
    getCustomValuesAndFieldsForLocation,
    locationStaticCustomValues,
    locationCustomValues,
    locationCustomFields,
  }
}
